import { useState, useEffect } from "react";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Users, ListTodo, Trophy, Settings, BarChart3, Loader2 } from "lucide-react";
import { TeamMembersList } from "@/components/team/TeamMembersList";
import { TeamTasksManagement } from "@/components/team/TeamTasksManagement";
import { TeamPerformanceMetrics } from "@/components/team/TeamPerformanceMetrics";
import { TeamSettings } from "@/components/team/TeamSettings";
import { TeamMemberStatistics } from "@/components/team/TeamMemberStatistics";
import { Navigate } from "react-router-dom";

interface TeamInfo {
  id: string;
  name: string;
  team_code: string;
  organization_id: string;
  team_leader_id: string | null;
}

export default function TeamManagement() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isTeamLeader, setIsTeamLeader] = useState(false);
  const [teamInfo, setTeamInfo] = useState<TeamInfo | null>(null);

  useEffect(() => {
    if (user) {
      checkTeamLeaderStatus();
    }
  }, [user]);

  const checkTeamLeaderStatus = async () => {
    if (!user) return;

    try {
      // Get user's team
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('team_id')
        .eq('user_id', user.id)
        .single();

      if (profileError || !profile?.team_id) {
        setLoading(false);
        return;
      }

      // Check if user is team leader
      const { data: team, error: teamError } = await supabase
        .from('teams')
        .select('*')
        .eq('id', profile.team_id)
        .single();

      if (teamError) {
        console.error('Error fetching team:', teamError);
        setLoading(false);
        return;
      }

      setTeamInfo(team);
      setIsTeamLeader(team.team_leader_id === user.id);
      
      // Also check if user is team moderator
      const { data: moderatorRole } = await supabase
        .from('team_member_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('team_id', profile.team_id)
        .eq('role', 'moderator')
        .maybeSingle();

      if (moderatorRole) {
        setIsTeamLeader(true); // Moderators also have management access
      }

    } catch (error) {
      console.error('Error checking team leader status:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!isTeamLeader || !teamInfo) {
    return <Navigate to="/" replace />;
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <AppSidebar />
        <main className="flex-1 flex flex-col">
          <header className="h-12 flex items-center border-b bg-card/80 backdrop-blur-sm sticky top-0 z-40">
            <SidebarTrigger className="ml-2" />
            <div className="flex-1 px-4">
              <h1 className="text-lg font-bold">Team verwalten</h1>
            </div>
          </header>
          <div className="flex-1 p-6">
            <div className="max-w-6xl mx-auto space-y-6">
              {/* Header */}
              <div className="space-y-2">
                <h1 className="text-3xl font-bold tracking-tight">Team verwalten</h1>
                <p className="text-muted-foreground">
                  {teamInfo.name} • <span className="font-mono text-sm">{teamInfo.team_code}</span>
                </p>
              </div>

              {/* Tabs */}
              <Tabs defaultValue="members" className="space-y-6">
                <TabsList className="grid w-full grid-cols-5 lg:w-auto lg:inline-grid">
                  <TabsTrigger value="members" className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    <span className="hidden sm:inline">Mitglieder</span>
                  </TabsTrigger>
                  <TabsTrigger value="tasks" className="flex items-center gap-2">
                    <ListTodo className="h-4 w-4" />
                    <span className="hidden sm:inline">Aufgaben</span>
                  </TabsTrigger>
                  <TabsTrigger value="performance" className="flex items-center gap-2">
                    <Trophy className="h-4 w-4" />
                    <span className="hidden sm:inline">Leistungen</span>
                  </TabsTrigger>
                  <TabsTrigger value="statistics" className="flex items-center gap-2">
                    <BarChart3 className="h-4 w-4" />
                    <span className="hidden sm:inline">Statistiken</span>
                  </TabsTrigger>
                  <TabsTrigger value="settings" className="flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    <span className="hidden sm:inline">Einstellungen</span>
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="members">
                  <TeamMembersList teamId={teamInfo.id} />
                </TabsContent>

                <TabsContent value="tasks">
                  <TeamTasksManagement teamId={teamInfo.id} />
                </TabsContent>

                <TabsContent value="performance">
                  <TeamPerformanceMetrics teamId={teamInfo.id} />
                </TabsContent>

                <TabsContent value="statistics">
                  <TeamMemberStatistics teamId={teamInfo.id} organizationId={teamInfo.organization_id} />
                </TabsContent>

                <TabsContent value="settings">
                  <TeamSettings teamInfo={teamInfo} onUpdate={checkTeamLeaderStatus} />
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
